define("shared/utils/media-adjustments/format-violation-message", ["exports", "shared/utils/media-adjustments/bytes-pretty-string", "shared/utils/media-adjustments/format-platform-name"], function (_exports, _bytesPrettyString, _formatPlatformName) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.collapseResizeViolations = collapseResizeViolations;
  _exports.formatAutoFixBulletCopy = formatAutoFixBulletCopy;
  _exports.formatViolationBullet = formatViolationBullet;
  _exports.formatViolationBullets = formatViolationBullets;
  _exports.isResizeBullet = isResizeBullet;
  0; //eaimeta@70e063a35619d71f0,"shared/utils/media-adjustments/bytes-pretty-string",0,"shared/utils/media-adjustments/format-platform-name"eaimeta@70e063a35619d71f
  const RESIZE_VIOLATION_NAMES = ['max_long_edge', 'max_short_edge', 'max_width', 'max_height', 'max_combined_dimensions'];
  function isSquare(width, height) {
    return width > 0 && width === height;
  }
  function numericActual(violation) {
    return typeof violation.actual === 'number' ? violation.actual : undefined;
  }
  function deriveResizeDimensions(longEdge, shortEdge, sourceDimensions) {
    const width = longEdge.width ?? shortEdge.width;
    const height = longEdge.height ?? shortEdge.height;
    if (width && height) {
      return {
        width,
        height
      };
    }
    const longActual = numericActual(longEdge) ?? 0;
    const shortActual = numericActual(shortEdge) ?? 0;
    if (longEdge.axis === 'both' || shortEdge.axis === 'both') {
      const squareDimension = Math.max(longActual, shortActual);
      return {
        width: squareDimension,
        height: squareDimension
      };
    }
    if (longEdge.axis === 'width' || shortEdge.axis === 'height') {
      return {
        width: longActual,
        height: shortActual
      };
    }
    if (longEdge.axis === 'height' || shortEdge.axis === 'width') {
      return {
        width: shortActual,
        height: longActual
      };
    }
    if (longActual >= shortActual) {
      return {
        width: longActual,
        height: shortActual
      };
    }
    if (shortActual > 0 || longActual > 0) {
      return {
        width: shortActual,
        height: longActual
      };
    }
    if (sourceDimensions) {
      return sourceDimensions;
    }
    return {
      width: 0,
      height: 0
    };
  }
  function combinedResizeLimits(width, height, longEdge, shortEdge) {
    const longLimit = typeof longEdge.expected === 'number' ? longEdge.expected : undefined;
    const shortLimit = typeof shortEdge.expected === 'number' ? shortEdge.expected : undefined;
    if (longLimit === undefined && shortLimit === undefined) {
      return {};
    }
    if (width >= height) {
      return {
        maxWidth: longLimit ?? shortLimit,
        maxHeight: shortLimit ?? longLimit
      };
    }
    return {
      maxWidth: shortLimit ?? longLimit,
      maxHeight: longLimit ?? shortLimit
    };
  }
  function collapseResizeViolations(violations, sourceDimensions) {
    const nonResizeViolations = violations.filter(violation => !RESIZE_VIOLATION_NAMES.includes(violation.name));
    const longEdge = violations.find(violation => violation.name === 'max_long_edge');
    const shortEdge = violations.find(violation => violation.name === 'max_short_edge');
    if (longEdge && shortEdge) {
      const {
        width,
        height
      } = deriveResizeDimensions(longEdge, shortEdge, sourceDimensions);
      if (isSquare(width, height)) {
        return [...nonResizeViolations, {
          ...shortEdge,
          name: 'max_short_edge',
          axis: 'both',
          width,
          height
        }];
      }
      const {
        maxWidth,
        maxHeight
      } = combinedResizeLimits(width, height, longEdge, shortEdge);
      return [...nonResizeViolations, {
        name: 'max_combined_dimensions',
        expected: 0,
        actual: 0,
        auto_fixable: true,
        fix_strategy: 'resize',
        width,
        height,
        max_dimension_width: maxWidth,
        max_dimension_height: maxHeight
      }];
    }
    const resizeViolations = violations.filter(violation => RESIZE_VIOLATION_NAMES.includes(violation.name));
    if (resizeViolations.length <= 1) {
      return violations;
    }
    return [...nonResizeViolations, ...resizeViolations];
  }
  function resizeI18nKey(violation) {
    const width = violation.width ?? 0;
    const height = violation.height ?? 0;
    if (violation.name === 'max_combined_dimensions') {
      return 'errors.autoFix.resizeBoth';
    }
    if (violation.name === 'max_long_edge') {
      if (violation.axis === 'height' || width > 0 && height > width && violation.axis !== 'width') {
        return 'errors.autoFix.resizeLongEdgeHeight';
      }
      return 'errors.autoFix.resizeLongEdgeWidth';
    }
    if (violation.name === 'max_short_edge') {
      if (violation.axis === 'both' || isSquare(width, height)) {
        return 'errors.autoFix.resizeSquare';
      }
      if (width >= height) {
        return 'errors.autoFix.resizeShortEdgeHeightLandscape';
      }
      return 'errors.autoFix.resizeShortEdgeWidthPortrait';
    }
    if (violation.name === 'max_width' || violation.name === 'max_height') {
      if (isSquare(width, height)) {
        return 'errors.autoFix.resizeSquare';
      }
      return 'errors.autoFix.resizeBoth';
    }
    return null;
  }
  function resolveViolationDimensions(violation, sourceDimensions) {
    let fallbackDimension = 0;
    if (typeof violation.actual === 'number') {
      fallbackDimension = violation.actual;
    } else if (typeof violation.expected === 'number') {
      fallbackDimension = violation.expected;
    }
    let width = violation.width ?? 0;
    let height = violation.height ?? 0;
    if (violation.name === 'max_long_edge' || violation.name === 'max_short_edge') {
      if (!width && violation.axis === 'width') {
        width = fallbackDimension;
      }
      if (!height && violation.axis === 'height') {
        height = fallbackDimension;
      }
      if (!width && !height && violation.axis === 'both') {
        width = fallbackDimension;
        height = fallbackDimension;
      }
    }
    if (!width && !height) {
      width = fallbackDimension;
      height = fallbackDimension;
    }
    if (sourceDimensions) {
      width = width || sourceDimensions.width;
      height = height || sourceDimensions.height;
    }
    return {
      width,
      height
    };
  }
  function formatViolationBullet(violation, platformKey, sourceDimensions) {
    const platform = (0, _formatPlatformName.formatPlatformName)(platformKey);
    if (violation.name === 'max_file_size' && typeof violation.expected === 'number') {
      const limitBytes = violation.expected;
      const actualBytes = Number(violation.actual);
      return {
        i18nKey: 'errors.autoFix.compress',
        i18nParams: {
          platform,
          actualSize: (0, _bytesPrettyString.bytesPrettyStringNearThreshold)(actualBytes, limitBytes),
          limit: (0, _bytesPrettyString.bytesPrettyString)(limitBytes)
        }
      };
    }
    const resizeKey = resizeI18nKey(violation);
    if (resizeKey && (typeof violation.expected === 'number' || violation.name === 'max_combined_dimensions')) {
      const {
        width,
        height
      } = resolveViolationDimensions(violation, sourceDimensions);
      if (violation.name === 'max_combined_dimensions') {
        const maxWidth = violation.max_dimension_width;
        const maxHeight = violation.max_dimension_height;
        if (maxWidth === undefined || maxHeight === undefined) {
          return null;
        }
        return {
          i18nKey: resizeKey,
          i18nParams: {
            platform,
            width,
            height,
            maxWidth,
            maxHeight
          }
        };
      }
      if (typeof violation.expected !== 'number') {
        return null;
      }
      return {
        i18nKey: resizeKey,
        i18nParams: {
          platform,
          maxWidth: violation.expected,
          maxHeight: violation.expected,
          width,
          height
        }
      };
    }
    if (violation.name === 'invalid_format') {
      const defaultTargetFormat = platformKey === 'tiktok' ? 'jpeg' : 'webp';
      return {
        i18nKey: 'errors.autoFix.convertFormat',
        i18nParams: {
          platform,
          actualFormat: String(violation.actual),
          targetFormat: violation.target_format ?? defaultTargetFormat
        }
      };
    }
    return null;
  }
  function formatViolationBullets(violations, platformKey, sourceDimensions) {
    return collapseResizeViolations(violations, sourceDimensions).map(violation => formatViolationBullet(violation, platformKey, sourceDimensions)).filter(bullet => bullet !== null);
  }
  function isResizeBullet(bullet) {
    return bullet.i18nKey.startsWith('errors.autoFix.resize');
  }
  function formatAutoFixBulletCopy(translate, bullet, adjusted) {
    const violationCopy = translate(bullet.i18nKey, bullet.i18nParams);
    if (!adjusted || !isResizeBullet(bullet)) {
      return violationCopy;
    }
    return `${violationCopy} ${translate('errors.autoFix.hasBeenResized', {
      newWidth: adjusted.width,
      newHeight: adjusted.height
    })}`;
  }
});